import { Form } from '@ant-design/compatible';
import { Col, Dropdown, Menu, Row } from 'antd';
import Alerts from 'common/alerts/containers/alert-container';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
// eslint-disable-next-line max-len
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import Table from 'common/table/legacy-table';
import {
  Button,
  DatePicker,
  DropdownButton,
  Flex,
  FormItem,
  InputNumber,
  notification,
  TimePicker,
} from 'ui';
import {
  alertSeverities,
  blockTypes,
  publishSteps,
  tagTypes,
} from 'utils/app-configs';
import { getDuration } from 'utils/helpers';
import useDoNotShow from 'utils/hooks/useDoNotShow';
import { blockConfig, viewConfig } from '../../../config';
import { defaultView } from '../../../config/view';
import Badge from './block-badge';
import { getStatusLabelKey, renderLockTag } from './block-detail-popover';
import Tag from './block-tag';

const StyledBlock = styled.div`
  padding-top: 16px;
  margin-bottom: 10px;
`;

const StyledFormItem = styled(FormItem)`
  min-height: 0px;
  .ant-legacy-form-item-required::before {
    display: none;
  }
`;

const StyledAntdDropdownButton = styled(Dropdown.Button)`
  .ant-btn {
    text-transform: uppercase;
    color: white;
    background-color: ${p => p.theme.colors.themeSecondary};
    &:hover {
      opacity: 0.7;
      border-color: white;
    }
  }
`;

const StyledMenu = styled(Menu)`
  text-transform: uppercase;
  background-color: ${p => p.theme.colors.themeSecondary}
`;

const StyledMenuItem = styled(Menu.Item)`
  color: white;
  &:hover {
    opacity: 0.7;
    background-color: ${p => p.theme.colors.themeSecondary}
  }
`;

const Unit = styled.span`
  margin-left: 5px;
  font-size: 16px;
`;

const ConfirmButton = styled(Button)`
  color: white;
  background-color: #6f7e95;
  &:hover {
    opacity: 0.7;
    color: white;
    background-color: #6f7e95;
  }
`;

const StyledTimePicker = styled(TimePicker)`
  width: 125px !important;
`;

const CancelButton = styled(Button)`
  margin-right: 12px;
  width: 140px;
  text-transform: uppercase;
`;

const StyledLink = styled.a`
  font-size: 16px;
  font-weight: 400;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  &:active {
    color: #0000EE !important;
  }
  &:focus {
    color: #0000EE !important;
  }
`;

const StyledReference = styled.div`
  margin-bottom: 5px;
`;

const StyledDropdownButton = styled(DropdownButton)`
.ant-dropdown-button {
  .ant-btn {
    text-transform: uppercase;
  }
  .ant-btn:first-child {
    width: 160px;
  }
}
`;

const StyledTagText = styled.span`
  margin-left: 5px;
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.65);
`;

const StyledBorderedBlock = styled(StyledBlock)`
  border-top: 1px solid #D9E0E6;
`;

const BlockEditSidepanelContent = ({
  match,
  intl,
  block,
  form,
  isLoadingBlockData,
  isDoingAction,
  updateBlock,
  isNew = false,
  view = {},
  onEngineerChange,
  markDateConfirmed,
  publishToSAP,
  readOnly,
  close,
  history,
  splitBlock,
  deletePlanning,
  markDateUnconfirmed,
  restoreBackup,
  recalculateTravelTime,
  openPlanningAndView,
}) => {
  const { getFieldDecorator } = form;
  const [doNotShowReschedule, checkbox] = useDoNotShow('confirmedToTentative');
  const transformValues = values => values?.filter(v => !v.isRegion);

  const handleRedirectPB = (planningId) => {
    openPlanningAndView({
      id: planningId,
      type: blockTypes.planning,
      viewId: defaultView.id,
      history,
      boardId: viewConfig.boardIdTypes.service,
      intl,
    });
  };

  const renderReferences = ref =>
    <StyledReference>
      <StyledFormItem
        label={intl.formatMessage({ id: `planningBoard.blocks.${ref.type.toUpperCase()}` })}
      />
      <StyledLink
        // eslint-disable-next-line max-len
        href={`/${ref.url}?context=${block?.objectId}`}
      >
        {ref.description}
      </StyledLink>
    </StyledReference>;

  const renderLock = (hasError, isPublishing) =>
    <>
      {renderLockTag(
        block?.lock,
        block?.isAssignedPlanner,
        hasError,
        block?.isDraft,
        isPublishing,
      )}

      <StyledTagText>
        {block.lock && block.lock.lockedReason !== 'readonly' ?
          `${block?.isAssignedPlanner ?
            intl.formatMessage({ id: 'planningBoard.blocks.YOU' }) :
            block.lock.lockedByName} ${block.lock.lockedOn ?
              `(${moment(block.lock.lockedOn).fromNow()})` :
              ''}` :
          `${block?.isAssignedPlanner ?
            intl.formatMessage({ id: 'planningBoard.blocks.YOU' }) :
            block?.assignedPlannerName} ${block.draftSince ?
              `(${moment(block.draftSince).fromNow()})` :
              ''}`
        }
      </StyledTagText>
    </>;

  const renderStatusLock = (hasError, isPublishing, isDraft) =>
    hasError || isPublishing || isDraft ?
      <StyledBorderedBlock>
        <StyledFormItem
          label={
            intl.formatMessage({
              id: `planningBoard.blocks.${getStatusLabelKey(hasError, isPublishing)}`,
            })
          }
        />
        {renderLock(hasError, isPublishing)}
      </StyledBorderedBlock> :
      null;

  const getSplitJobsColumns = () => [
    {
      label: intl.formatMessage({ id: 'order.fieldEngineer.FIELD_SERVICE_ENGINEER' }),
      dataIndex: 'laneDescription',
    },
    {
      label: intl.formatMessage({ id: 'planningBoard.blocks.START_DATE' }),
      dataIndex: 'startDate',
      format: 'date',
    },
    {
      label: intl.formatMessage({ id: 'planningBoard.blocks.START_LABOUR' }),
      dataIndex: 'startLabour',
    },
    {
      label: intl.formatMessage({ id: 'planningBoard.blocks.DURATION_LABOUR' }),
      dataIndex: 'durationLabour',
      renderValueFn: value => getDuration(value),
    },
    {
      label: intl.formatMessage({ id: 'planningBoard.blocks.DURATION_TRAVEL' }),
      dataIndex: 'durationTravel',
      renderValueFn: value => getDuration(value),
    },
    {
      renderValueFn: (v, row) => <StyledAntdDropdownButton
        onClick={() => handleRedirectPB(row.planningId)}
        overlay={<StyledMenu>
          <StyledMenuItem
            key='open_pb'
            onClick={() => handleRedirectPB(row.planningId)}
          >
            {intl.formatMessage({ id: 'planningBoard.blocks.OPEN_PB_PLANNING' })}
          </StyledMenuItem>
          <StyledMenuItem
            key='open_order'
            onClick={() =>
              history.push(
                // eslint-disable-next-line max-len
                `${history.location.pathname.replace(encodeURI(match.params.planningId), row.planningId)}`,
              )
            }
          >
            {intl.formatMessage({ id: 'planningBoard.blocks.OPEN_ORDER_PLANNING' })}
          </StyledMenuItem>
        </StyledMenu>}
      >
        {intl.formatMessage({ id: 'planningBoard.blocks.OPEN_PB_PLANNING' })}
      </StyledAntdDropdownButton>,
    },
  ];

  getFieldDecorator('laneId', {
    initialValue: isNew ? match.params.laneId : block?.laneId,
  });
  const engineerNumberValidator = getFieldDecorator('technicianNumber', {
    initialValue: isNew ?
      match.params.laneId :
      block?.laneId,
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.FIELD_ENGINEER' }) }],
  });
  const startDateValidator = getFieldDecorator('startDate', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.START_DATE' }) }],
    initialValue: isNew ? moment(match.params.date, 'YYYYMMDD') : moment(block?.start),
  });
  const durationTravelToValidator = getFieldDecorator('travelTo', {
    rules: [
      {
        required: true,
        message: intl.formatMessage({ id: 'planningBoard.blocks.DURATION_ERROR' }),
      },
    ],
    initialValue:
      // eslint-disable-next-line
      parseFloat(moment.duration(block?.subBlocks?.find(b => b.activityType === blockConfig.activityTypes.travelTo)?.duration).asHours().toFixed(2)),
  });
  const durationTravelFromValidator = getFieldDecorator('travelFrom', {
    rules: [
      {
        required: true,
        message: intl.formatMessage({ id: 'planningBoard.blocks.DURATION_ERROR' }),
      },
    ],
    initialValue:
      // eslint-disable-next-line
      parseFloat(moment.duration(block?.subBlocks?.find(b => b.activityType === blockConfig.activityTypes.travelFrom)?.duration).asHours().toFixed(2)),
  });
  const durationLabourValidator = getFieldDecorator('durationLabour', {
    rules: [
      {
        required: true,
        message: intl.formatMessage({ id: 'planningBoard.blocks.DURATION_ERROR' }),
      },
    ],
    initialValue:
      // eslint-disable-next-line
      parseFloat(moment.duration(block?.subBlocks?.find(b => b.activityType === blockConfig.activityTypes.labour)?.duration).asHours().toFixed(2)),
  });
  const startWorkingDay = view?.parameters?.startWorkingDay || '08:00:00';
  const startTimeValidator = getFieldDecorator('startTime', {
    rules: [{ required: true, message: intl.formatMessage({ id: 'rules.START_TIME' }) }],
    initialValue: isNew ?
      moment(startWorkingDay, 'HH:mm:ss') :
      moment(
        block?.start,
      ),
  });

  const orderStatusTag = block.tags?.find(t => t.type === tagTypes.orderStatus);
  const orderTypeTag = block.tags?.find(t => t.type === tagTypes.orderType);
  const disabled = isLoadingBlockData || isDoingAction || readOnly;
  const hasError =
    block?.operationInfo?.errors?.length ||
    block?.operationInfo?.isTimedOut ||
    block?.operationInfo?.isStale;
  const isPublishing = (
    block?.operationInfo?.status >= publishSteps.unknown.status &&
    block?.operationInfo?.status < publishSteps.done.status
  ) ||
    block?.operationInfo?.status === publishSteps.waitCycle.status;

  return isLoadingBlockData ?
    <Spinner /> :
    <>
      <StyledBlock>
        <Form layout='vertical'>
          <StyledFormItem
            label={
              intl.formatMessage({ id: 'order.fieldEngineer.FIELD_SERVICE_ENGINEER' })
            }
            data-test-id='block-sp-fse'
          >
            {engineerNumberValidator(
              <ReferenceDataDropdown
                width={'300px'}
                isSorted={true}
                disabled={disabled}
                placeholder={
                  intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })
                }
                type='order'
                name='workCenters'
                valueKey='id'
                valueLabel='name'
                onObjectChanged={onEngineerChange}
                transformValues={transformValues}
                serviceCenterId={block?.serviceCenterId}
                isPlanningBoard={true}
                useBodyAsParent={true}
              />,
            )}
          </StyledFormItem>
          <StyledFormItem
            label={intl.formatMessage({ id: 'equipment.general.REQUESTED_START_DATE' })}
          >
            {
              startDateValidator(
                <DatePicker
                  disabled={disabled}
                  allowPast={false}
                  useBodyAsParent={true}
                  data-test-id='block-sp-start-date'
                />,
              )
            }
          </StyledFormItem>
          <Row>
            <Col span={6}>
              <StyledFormItem
                label={
                  intl.formatMessage({ id: 'planningBoard.blocks.START_TIME' })
                }
                colon={false}
              >
                {startTimeValidator(
                  <StyledTimePicker
                    allowClear={false}
                    showSecond={false}
                    format='HH:mm'
                    disabled={disabled}
                    minuteStep={15}
                    useBodyAsParent={true}
                    data-test-id='block-sp-start-time'
                  />,
                )}
              </StyledFormItem>
            </Col>
            <Col span={6}>
              <StyledFormItem
                label={
                  intl.formatMessage({ id: 'planningBoard.blocks.DURATION_TRAVEL_TO' })
                }
                colon={false}
              >
                {durationTravelToValidator(
                  <InputNumber
                    min={0}
                    disabled={disabled}
                    data-test-id='block-sp-duration-travel-to'
                    precision={2}
                  />,
                )}
                <Unit><FormattedMessage id='equipment.taskList.uom.STD' /></Unit>
              </StyledFormItem>
            </Col>
            <Col span={6}>
              <StyledFormItem
                label={intl.formatMessage({ id: 'planningBoard.blocks.DURATION_LABOUR' })}
                colon={false}
              >
                {durationLabourValidator(
                  <InputNumber
                    min={0}
                    disabled={disabled}
                    data-test-id='block-sp-duration-labour'
                    precision={2}
                  />,
                )}
                <Unit><FormattedMessage id='equipment.taskList.uom.STD' /></Unit>
              </StyledFormItem>
            </Col>
            <Col span={6}>
              <StyledFormItem
                label={
                  intl.formatMessage({ id: 'planningBoard.blocks.DURATION_TRAVEL_FROM' })
                }
                colon={false}
              >
                {durationTravelFromValidator(
                  <InputNumber
                    min={0}
                    disabled={disabled}
                    data-test-id='block-sp-duration-travel-from'
                    precision={2}
                  />,
                )}
                <Unit><FormattedMessage id='equipment.taskList.uom.STD' /></Unit>
              </StyledFormItem>
            </Col>
          </Row>
          <Flex jcfs marginTop={20}>
            <CancelButton
              type='secondary'
              light onClick={close}
              disabled={readOnly}
              data-test-id='btn-planning-sp-cancel'
            >
              {intl.formatMessage({ id: 'common.CANCEL' })}
            </CancelButton>
            <StyledDropdownButton
              dataTestId='dropdown-block-sp-save'
              text={intl.formatMessage({ id: 'planningBoard.blocks.SAVE_PLANNING' })}
              uppercase={true}
              onClick={updateBlock}
              disabled={isLoadingBlockData || isDoingAction || readOnly}
              subItems={[
                {
                  id: blockConfig.blockActions.savePlanning,
                  text: intl.formatMessage({ id: 'planningBoard.blocks.SAVE_PLANNING' }),
                  onClick: updateBlock,
                },
                {
                  id: blockConfig.blockActions.markDateConfirmed,
                  text:
                    intl.formatMessage({ id: 'planning.actions.confirmPlanning.description' }),
                  onClick: () => markDateConfirmed({
                    boardId: viewConfig.boardIdTypes.service,
                    planningId: block.id,
                  }),
                },
                {
                  id: blockConfig.blockActions.publishToSAP,
                  text: intl.formatMessage({ id: 'planning.actions.publishToSap.description' }),
                  onClick: () => publishToSAP({
                    boardId: viewConfig.boardIdTypes.service,
                    planningIds: [block.id],
                  }),
                },
                {
                  id: blockConfig.blockActions.splitBlock,
                  text: intl.formatMessage({ id: 'planning.actions.splitJob.description' }),
                  onClick: () => splitBlock({
                    boardId: viewConfig.boardIdTypes.service,
                    planningId: block.id,
                    refetch: true,
                  }),
                },
                {
                  id: blockConfig.blockActions.restoreBackup,
                  text: intl.formatMessage({ id: 'planning.actions.restorePlanning.description' }),
                  onClick: () => restoreBackup({
                    planningId: block.id,
                    boardId: viewConfig.boardIdTypes.service,
                  }),
                },
                {
                  id: blockConfig.blockActions.recalculateTravelTime,
                  text: intl.formatMessage({
                    id: 'planning.actions.recalculateTravelTime.description',
                  }),
                  onClick: () => recalculateTravelTime({
                    planningId: block.id,
                    boardId: viewConfig.boardIdTypes.service,
                  }),
                },
                {
                  id: blockConfig.blockActions.markDateUnconfirmed,
                  text:
                    intl.formatMessage({ id: 'planning.actions.unConfirmPlanning.description' }),
                  onClick: () => markDateUnconfirmed({
                    boardId: viewConfig.boardIdTypes.service,
                    planningId: block.id,
                    successCb: block.showAs === 'active' &&
                      block.type === blockConfig.types.fieldService ?
                      () => {
                        if (!doNotShowReschedule) {
                          notification.info({
                            key: 'confirmedToTentative',
                            placement: 'top',
                            btn: <ConfirmButton
                              type='primary'
                              size='small'
                              onClick={() => notification.destroy('confirmedToTentative')}
                            >
                              {intl.formatMessage({ id: 'common.OK' })}
                            </ConfirmButton>,
                            message:
                              intl.formatMessage({
                                id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE_TITLE',
                              }),
                            description: <>
                              {intl.formatMessage({
                                id: 'planningBoard.blocks.PLANNED_TO_TENTATIVE',
                              })}
                              {checkbox}
                            </>,
                            duration: 0,
                            className: 'extended-notification',
                          });
                        }
                      } :
                      null,
                  }),
                },
                {
                  id: blockConfig.blockActions.deletePlanning,
                  text: intl.formatMessage({ id: 'planning.actions.delete.description' }),
                  onClick: deletePlanning,
                },
              ]?.filter(a =>
                block?.actions?.find(ba => ba.id === a.id) ||
                a.id === blockConfig.blockActions.savePlanning,
              )?.sort((a, b) => {
                const first = block?.actions?.find(ba => ba.id === a.id);
                const second = block?.actions?.find(ba => ba.id === b.id);
                if (a.id === blockConfig.blockActions.savePlanning) {
                  return -1;
                }
                return first?.priority - second?.priority;
              })}
            >
              <FormattedMessage id='planningBoard.blocks.SAVE_PLANNING' />
            </StyledDropdownButton>
          </Flex>
        </Form>
      </StyledBlock>
      {renderStatusLock(hasError, isPublishing, block?.isDraft)}
      {block?.splitJobs?.length ?
        <StyledBorderedBlock>
          <StyledFormItem label={intl.formatMessage({ id: 'planningBoard.blocks.RELATED_JOBS' })} />
          <Table
            hasRowSelector={false}
            columns={getSplitJobsColumns()}
            data={block?.splitJobs}
            isLoading={isLoadingBlockData}
            scroll={{ x: 1000 }}
          />
        </StyledBorderedBlock> :
        null
      }
      <StyledBorderedBlock>
        <StyledFormItem label={intl.formatMessage({ id: 'planningBoard.blocks.ALERTS' })} />
        <Alerts
          blockAlerts={block?.alert?.alerts?.map(a => ({
            ...a,
            severity: blockConfig.blockSeverities[a.level?.toUpperCase()],
            name: a.description,
          })) || []}
          maxVisibleAlerts={block?.alert?.alerts?.length}
          needsTitle={false}
          largeTag={true}
          blockId={block?.id}
          laneId={block?.laneId}
          disabled={disabled}
        />
      </StyledBorderedBlock>
      <StyledBorderedBlock>
        <StyledFormItem
          label={intl.formatMessage({ id: 'planningBoard.blocks.ORDER_TYPE' })}
        />
        {orderTypeTag ?
          <>
            <Tag
              color={alertSeverities[blockConfig.blockSeverities.INFO].colorStatus}
              text={orderTypeTag.description}
              shortText={orderTypeTag.shortDescription}
              showShortText={true}
              large={true}
            />
            <StyledTagText>{orderTypeTag.description}</StyledTagText>
          </> :
          null
        }
        <StyledFormItem
          label={intl.formatMessage({ id: 'planningBoard.blocks.ORDER_STATUS' })}
        />
        {orderStatusTag ?
          <>
            <Tag
              color={alertSeverities[blockConfig.blockSeverities.INFO].colorStatus}
              text={orderStatusTag.description}
              shortText={orderStatusTag.shortDescription}
              showShortText={true}
              large={true}
            />
            <StyledTagText>{orderStatusTag.description}</StyledTagText>
          </> :
          null
        }
        {block?.badges?.length ?
          <>
            <StyledFormItem
              label={intl.formatMessage({ id: 'planningBoard.blocks.ADDITIONAL_INFO' })}
            />
            {block.badges?.map((b, i) =>
              <>
                <Badge icon={b.icon} iconStyle={b.iconStyle} />
                <StyledTagText>
                  {b.description}{i + 1 < block?.badges?.length ? '; ' : ''}
                </StyledTagText>
              </>,
            )}
          </> :
          null
        }
      </StyledBorderedBlock>
      <StyledBorderedBlock>
        {block?.references?.map(t =>
          renderReferences(t),
        )}
      </StyledBorderedBlock>
    </>;
};

BlockEditSidepanelContent.propTypes = {
  match: PropTypes.object,
  intl: PropTypes.object,
  block: PropTypes.object,
  form: PropTypes.object,
  isLoadingBlockData: PropTypes.bool,
  isDoingAction: PropTypes.bool,
  updateBlock: PropTypes.func,
  isNew: PropTypes.bool,
  publishToSAP: PropTypes.func,
  markDateConfirmed: PropTypes.func,
  view: PropTypes.object,
  readOnly: PropTypes.bool,
  onEngineerChange: PropTypes.func,
  close: PropTypes.func,
  splitBlock: PropTypes.func,
  deletePlanning: PropTypes.func,
  markDateUnconfirmed: PropTypes.func,
  restoreBackup: PropTypes.func,
  recalculateTravelTime: PropTypes.func,
  openPlanningAndView: PropTypes.func,
  history: PropTypes.func,
};

export default withRouter(injectIntl(BlockEditSidepanelContent));
